import React from "react";
import {createPortal} from "react-dom";
import {Link} from "gatsby";
import {languageInfo} from "../../resources/languages";

// Language switch in the top bar's user slot. The bar is rendered outside the i18next provider
// (wrap-page-element.js wraps the plugin's wrapper), so the page's i18n context comes in as a prop
// and the links are built here instead of with the plugin's Link.

// gatsby-plugin-react-i18next keeps the chosen language under this key and, on an unprefixed
// (default-language) page, redirects to whatever it holds. Picking a language must update it,
// or choosing English from /de/... would bounce straight back to /de/.
const LANGUAGE_KEY = "gatsby-i18next-language";

const remember = (code) => {
    try {
        window.localStorage.setItem(LANGUAGE_KEY, code);
    } catch (e) {
        // Storage blocked - the link still works, the plugin just falls back to the browser language.
    }
};

const pathFor = (i18n, code) =>
    (code === i18n.defaultLanguage ? "" : "/" + code) + i18n.originalPath;

const GlobeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9"/>
        <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z"/>
    </svg>
);

const LanguageSelector = ({i18n}) => {
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
        if (!open) return undefined;
        const closeOnEscape = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [open]);

    if (!i18n || !i18n.languages || i18n.languages.length < 2) return null;

    const current = languageInfo(i18n.language);

    return (
        <div className="userloginbox langbox">
            <button type="button"
                    className="userlogin langbtn"
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    title={current.title}
                    onClick={() => setOpen(true)}>
                <GlobeIcon/>
                <span className="langlabel">{current.name}</span>
                <span className="langcode">{i18n.language.toUpperCase()}</span>
            </button>

            {/* Portaled to <body>: the bar's stylesheet targets generic descendants (ul, a, li)
                and would leak into the dialog, which follows the app's design instead. */}
            {open && createPortal(
                <div className="modal-bg" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
                    <div className="modal langmodal" role="dialog" aria-modal="true" aria-labelledby="lang-title">
                        <div className="mh">
                            <span id="lang-title">{current.title}</span>
                            <button type="button" aria-label="Close" onClick={() => setOpen(false)}>×</button>
                        </div>
                        <ul className="langgrid">
                            {i18n.languages.map(code => {
                                const info = languageInfo(code);
                                const active = code === i18n.language;
                                return (
                                    <li key={code}>
                                        <Link to={pathFor(i18n, code)}
                                              lang={code}
                                              hrefLang={code}
                                              className={active ? "langitem on" : "langitem"}
                                              aria-current={active ? "true" : undefined}
                                              onClick={() => {
                                                  remember(code);
                                                  setOpen(false);
                                              }}>
                                            <span className="lname" dir="auto">{info.name}</span>
                                            <span className="lcode">{code.toUpperCase()}</span>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default LanguageSelector;
